import { ArrayUtil, Collection } from "@mcleod/core";
import { ComponentConstructor, ComponentProps } from "@mcleod/components";
import { ExampleGroup } from "../ExampleGroup";
import { ExampleOnCreateCallback } from "../ExampleOnCreateCallback";
import { FinalizedExample } from "../FinalizedExample";
import { LiveDemoDef } from "../LiveDemoDef";

/**
 * An instance of AbstractComponentDemoExamples defines the property-level examples, and additional examples, for a
 * particular component.
 * - Subclasses should override *registerExamples()* (and call *super.registerExamples()* within the override) to add
 *   property examples and/or additional examples.
 * - *propsForAllExamples* is a props object that will apply to every component created in the demo.  For example, a
 *   Label likely always needs text, or a Panel might always want to render a border so the user can see it.
 */
export abstract class AbstractComponentDemoExamples {
    private propertyExamples = new Map<string,ExampleGroup>();
    private addlExamples = new Map<string,ExampleGroup>();
    private _addlExampleDefs: Collection<any> = {};
    private _liveDemoDefs: Collection<LiveDemoDef> = {};

    constructor() {
        this.registerExamples();
    }

    get addlExampleDefs(): Collection<any> {
        return this._addlExampleDefs;
    }

    get liveDemoDefs(): Collection<LiveDemoDef> {
        return this._liveDemoDefs;
    }

    protected registerExamples() {
        this.registerPropertyExamples("cursor", {
            examples: [
                { value: undefined },
                { value: "pointer" },
            ]
        });
        this.registerPropertyExamples("enabled", {
            examples: [
                { value: undefined },
                { value: false },
            ]
        });
        this.registerPropertyExamples("padding", {
            examples: [
                { value: 0 },
                { value: 4 },
                { value: 16 }
            ]
        });
        this.registerPropertyExamples("tooltip", {
            examples: [
                { value: "Here is some helpful text to explain what to do with this component!" }
            ]
        });
        this.registerPropertyExamples("visible", {
            examples: [
                { value: undefined },
                { value: false },
            ]
        });
    }

    protected registerPropertyExamples(propName: string, exampleGroup: ExampleGroup) {
        this.propertyExamples.set(propName, exampleGroup);
    }

    protected registerAddlExamples(name: string, description: string, exampleGroup: ExampleGroup) {
        this.addlExamples.set(name, exampleGroup);
        // The purpose of addlExampleDefs is to provide a collection similar to the property definition collection.  The
        // name and description values stored in addlExampleDefs will be used to render the line panel for that
        // particular entry in the additional examples section.
        this.addlExampleDefs[name] = { description };
    }

    protected registerLiveDemos(...demos: LiveDemoDef[]) {
        demos.forEach(demo => {
            this.liveDemoDefs[demo.name] = demo;
        });
    }

    /**
     * This method returns all of the examples for a single component property, or a single example in the additional
     * examples section.
     * @param name the property for which examples should be retrieved, or the name of the additional example.
     * @returns an array containing a FinalizedExample object for each single example to render.
     */
    get(name: string, componentClassRef: ComponentConstructor, componentName: string):
        FinalizedExample[] {
        const exampleGroup = this.propertyExamples.get(name) ?? this.addlExamples.get(name);
        const result: FinalizedExample[] = [];
        if (exampleGroup != null && !ArrayUtil.isEmptyArray(exampleGroup.examples)) {
            exampleGroup.examples.forEach((example) => {
                const exampleInput: FinalizedExample = {
                    name: name,
                    value: example.value,
                    description: example.description,
                    props: {},
                    onCreate: [],
                    componentClassRef,
                    componentName
                };
                exampleInput.props = { ...this.propsForAllExamples, ...exampleGroup.props, ...example.props };
                if (this.onCreate != null)
                    exampleInput.onCreate.push(this.onCreate);
                if (exampleGroup.onCreate != null)
                    exampleInput.onCreate.push(exampleGroup.onCreate);
                if (example.onCreate != null)
                    exampleInput.onCreate.push(example.onCreate);
                result.push(exampleInput);
            });
        }
        return result;
    }

    protected get propsForAllExamples(): Partial<ComponentProps> {
        return {};
    }

    protected get onCreate(): ExampleOnCreateCallback {
        return null;
    }
}
